import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import ClickAwayListener from 'react-click-away-listener';
import MoveIcon from '@learningblocks/exp-constants/lib/Images/move.svg';
import {Image, CloseButton} from '@learningblocks/storybook-components';
import {CONST} from './Tooltip.constants';
import {ContentsWrap} from '../ContentsWrap';
import {
  calculateTooltipPosition,
  getNearestScrollingAncestor,
} from './Tooltip.utils';
import {throttle} from '@learningblocks/exp-constants/lib/Utilities';
import style from './style.module.scss';

const removeMd = require('remove-markdown');
const truncate = require('html-truncate');

/**
 * Tooltip
 *  - Tooltip of the glossary tooltip component.
 */

export class Tooltip extends React.Component {
  constructor(props) {
    super(props);
    this.wrapRef = React.createRef();
    this.state = {
      classList: [style.tooltip, props.overloadClass],
      startOffset: {x: null, y: null},
      style: {
        left: CONST.AUTO,
        bottom: CONST.AUTO,
        top: CONST.AUTO,
        right: CONST.AUTO,
        transform: CONST.INITIAL_TRANSFORM,
      },
    };
    this.scrollingParent = null;
  }

  setNewCordinates = () => {
    if (
      this.wrapRef.current &&
      this.state.style.transform === CONST.INITIAL_TRANSFORM
    ) {
      let {position, classList} = calculateTooltipPosition(
        this.wrapRef.current
      );
      let newStyle = {
        ...this.state.style,
        ...position,
      };
      let newClassList = [
        style.tooltip,
        ...classList,
        this.props.overloadClass,
      ];
      this.setState({style: newStyle, classList: newClassList});
    }
  };

  componentDidMount() {
    window.addEventListener(CONST.EVENT_RESIZE, this.onResize);
    window.addEventListener(CONST.EVENT_SCROLL, this.onScroll);
    window.addEventListener(CONST.EVENT_MOUSE_UP, this.onDragEnd);
    this.bindParentScrollEvent();
    this.setNewCordinates();
  }

  componentWillUnmount() {
    this.unbindParentScrollEvent();
    window.removeEventListener(CONST.EVENT_MOUSE_UP, this.onDragEnd);
    window.removeEventListener(CONST.EVENT_RESIZE, this.onResize);
    window.removeEventListener(CONST.EVENT_SCROLL, this.onScroll);
  }

  componentDidUpdate(prevProps) {
    if (this.props.tooltipApi.loading !== prevProps.tooltipApi.loading) {
      this.setNewCordinates();
    }
  }

  bindParentScrollEvent = () => {
    if (this.wrapRef.current && this.scrollingParent === null) {
      this.scrollingParent = getNearestScrollingAncestor(this.wrapRef.current);
      this.scrollingParent.addEventListener(
        CONST.EVENT_SCROLL,
        this.offsetParentScroll
      );
    }
  };

  unbindParentScrollEvent = () => {
    if (this.scrollingParent) {
      this.scrollingParent.removeEventListener(
        CONST.EVENT_SCROLL,
        this.offsetParentScroll
      );
      this.scrollingParent = null;
    }
  };

  offsetParentScroll = (event) => {
    throttle(this.setNewCordinates);
  };

  onResize = (event) => {
    throttle(this.setNewCordinates);
  };

  onScroll = (event) => {
    throttle(this.setNewCordinates);
  };

  closeTooltip = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.close();
  };

  openGlossary = (event) => {
    this.closeTooltip(event);
    this.props.openGlossary(this.props.contentID);
  };

  renderTitle = () => <h4><ReactMarkdown rehypePlugins={[rehypeRaw]}>{this.props.title}</ReactMarkdown></h4>;

  renderDescription = () => {
    let {description} = this.props;
    // if (removeMd(description).length > CONST.MAX_DESCRIPTION_LENGTH) {
    //   description = description.substring(
    //     CONST.MIN_DESCRIPTION_LENGTH,
    //     CONST.MAX_DESCRIPTION_LENGTH
    //   );
    // }
      return (
        <p>
          <ReactMarkdown rehypePlugins={[rehypeRaw]} components={{p: React.Fragment}}>{truncate(description, CONST.MAX_DESCRIPTION_LENGTH, {ellipsis: "..."})}</ReactMarkdown>
          {" "}<span onClick={this.openGlossary}>read more</span>
        </p>
      );
    // } else {
    //   return (
    //     <p>
    //       <ReactMarkdown rehypePlugins={[rehypeRaw]} components={{p: React.Fragment}}>{description}</ReactMarkdown>
    //       <span onClick={this.openGlossary}>read more</span>
    //     </p>
    //   );
    // }
  };

  renderImage = () => {
    if (this.props.image) {
      return <Image src={this.props.image} alt={this.props.title} />;
    } else return null;
  };

  updateTooltipDragPosition = (event) => {
    let {x, y} = this.state.startOffset;
    let newStyle = {...this.state.style};
    newStyle.transform = `translate(${event.pageX - x}px,${event.pageY - y}px)`;
    this.setState({style: newStyle});
  };

  onDragStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.state.startOffset.x === null) {
      this.setState({startOffset: {x: event.pageX, y: event.pageY}});
    }
    window.addEventListener(
      CONST.EVENT_MOUSE_MOVE,
      this.updateTooltipDragPosition
    );
  };

  onDragEnd = (event) => {
    event.preventDefault();
    event.stopPropagation();
    window.removeEventListener(
      CONST.EVENT_MOUSE_MOVE,
      this.updateTooltipDragPosition
    );
  };

  render() {
    return (
      <ClickAwayListener onClickAway={this.closeTooltip}>
        <div
          ref={this.wrapRef}
          style={this.state.style}
          className={this.state.classList.join(' ')}>
          <div className={style.tooltip_header}>
            <Image
              onMouseDown={this.onDragStart}
              src={MoveIcon}
              alt='move tooltip card'
            />
            <CloseButton close={this.closeTooltip} />
          </div>
          <ContentsWrap {...this.props.tooltipApi} onRetry={this.props.onRetry}>
            <div className={style.tooltip_body}>
              {this.renderImage()}
              {this.renderTitle()}
              {this.renderDescription()}
            </div>
          </ContentsWrap>
        </div>
      </ClickAwayListener>
    );
  }
}

Tooltip.propTypes = {
  /**
   * Callback function executed when close button is clicked
   */
  close: PropTypes.func.isRequired,
  /**
   * Url to the image file to be displayed in item description
   */
  image: PropTypes.string,
  /**
   * Title string of the tooltip content
   */
  title: PropTypes.string.isRequired,
  /**
   * Description for the content.
   */
  description: PropTypes.string,
  /**
   * Function to invoke to open the glossary
   */
  openGlossary: PropTypes.func.isRequired,
  /**
   * Function to invoke when getDetailsApi fails
   */
  onRetry: PropTypes.func.isRequired,
  /**
   * Status of fetchTooltip api
   */
  tooltipApi: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.bool,
  }).isRequired,
};

Tooltip.defaultProps = {
  image: '',
  title: '',
  description: '',
  loading: false,
  tooltipApi: {loading: null, error: null},
  overloadClass: '',
};
